import type { LegacyRef } from 'react'
import React, { useEffect, useState, useTransition } from 'react'

import { useRouter } from 'next/navigation'

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Slide,
  Stack,
  Typography
} from '@mui/material'

import FacebookIcon from '@mui/icons-material/Facebook'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import type { TransitionProps } from '@mui/material/transitions'
import dayjs from 'dayjs'

import useFocusTrap from '@/hooks/useFocusTrap'
import ImagePreview from '@/components/layout/shared/ImagePreview'
import CustomButton from '@/components/layout/shared/CustomButton'
import 'dayjs/locale/id'
import ProgressBar from '@/components/ProgressBar'

dayjs.locale('id')

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />
})

interface DetailDialogProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  dataRow?: any
}
const NEXT_PUBLIC_URL_IMAGE = process.env.NEXT_PUBLIC_URL_IMAGE_REGIS

const DetailDialog: React.FC<DetailDialogProps> = ({ open, setOpen, dataRow }) => {
  const [loadingSimpan, setLoadingSimpan] = useState<boolean>(false)
  const focusTrapRef = useFocusTrap()

  const [imageUrl, setImageUrl] = useState('')

  useEffect(() => {
    if (!open) return
  }, [dataRow, open])

  const openModal = (value: string) => {
    setImageUrl(value)
  }

  const shareToWhatsApp = () => {
    const text = `Berita Duka Cita:\n\nTelah berpulang dengan damai,\n*${dataRow?.NAMA_M}*\n${dataRow?.NAMAASLI_M ? `(${dataRow?.NAMAASLI_M})` : ''}\n
Pada:
${dayjs(dataRow?.TGL_M).format('dddd, DD MMMM YYYY')}
Pukul ${dayjs(dataRow?.TGL_M).format('HH:mm')} WIB

Disemayamkan di:
TMC Eternal Home
${dataRow?.NAMA_RUANG}

Untuk informasi lebih lanjut dan pengiriman simpati duka secara digital, silahkan kunjungi:
${window.location.href}`

    const encodedText = encodeURIComponent(text)

    window.open(`https://wa.me/?text=${encodedText}`, '_blank')
  }

  const shareToFacebook = () => {
    const text = `Berita Duka Cita: Telah berpulang dengan damai, ${dataRow?.NAMA_M} ${
      dataRow?.NAMAASLI_M ? `(${dataRow?.NAMAASLI_M})` : ''
    }`

    const url = window.location.href

    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}&quote=${encodeURIComponent(text)}`

    // Open Facebook share dialog
    const width = 626
    const height = 436
    const left = (window.innerWidth - width) / 2
    const top = (window.innerHeight - height) / 2

    window.open(
      shareUrl,
      'facebook-share-dialog',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0`
    )
  }

  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const handleOpen = () => {
    sessionStorage.clear()

    startTransition(() => {
      sessionStorage.setItem('dataRegis', JSON.stringify(dataRow))
      router.push(`/order/funeral?id=` + dataRow?.NO_REG)
    })
  }

  return (
    <>
      <ImagePreview imageString={imageUrl} setImage={setImageUrl} />
      {isPending && <ProgressBar />}

      <Dialog
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        scroll='body'
        TransitionComponent={Transition}
      >
        <form
          noValidate
          autoComplete='off'
          ref={focusTrapRef as LegacyRef<HTMLFormElement> | undefined}
          tabIndex={-1}
          className='flex flex-col gap-5'
        >
          <DialogContent className='overflow-visible pbs-0 sm:pbe-2 sm:pli-5'>
            <div className='flex justify-between items-center mb-5 mt-5'>
              <Typography variant='h5'>Detail</Typography>
              <IconButton onClick={() => setOpen(false)} color='primary' size='small'>
                <i className='ri-close-line' />
              </IconButton>
            </div>

            <Grid container spacing={3}>
              {/* Right Side (di atas di mobile, di kanan di desktop) */}
              <Grid item xs={12} md={3} order={{ xs: 1, md: 2 }}>
                <Box textAlign='center'>
                  <img
                    src={dataRow?.PHOTO_M ? NEXT_PUBLIC_URL_IMAGE + dataRow?.PHOTO_M : '/images/icon/NoData.png'}
                    onClick={() => openModal(NEXT_PUBLIC_URL_IMAGE + dataRow?.PHOTO_M)}
                    alt='Profile'
                    style={{
                      width: '100px', // lebih lebar dari tinggi untuk oval horizontal
                      height: '120px',
                      border: '3px solid #1976d2', // sesuaikan dengan warna primary kamu
                      borderRadius: '50%',
                      objectFit: 'cover', // agar gambar tidak terdistorsi
                      marginBottom: '16px' // mb-2
                    }}
                  />
                  <Box mt={2}>
                    <Box p={1}>
                      <CustomButton variant='outlined' type='button' loading={false} onClick={handleOpen}>
                        Kirim Ucapan Duka
                      </CustomButton>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Left Content */}
              <Grid item xs={12} md={9} order={{ xs: 2, md: 1 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant='h5' fontWeight='bold' gutterBottom align='center'>
                    {dataRow?.NAMA_M}
                  </Typography>
                  <Typography variant='h6' fontStyle='italic' gutterBottom align='center'>
                    {dataRow?.NAMAASLI_M}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant='subtitle2' color='primary' gutterBottom>
                    TELAH BERPULANG DENGAN DAMAI
                  </Typography>

                  {[
                    ['Hari', dayjs(dataRow?.TGL_M).format('dddd')],
                    ['Tanggal', dayjs(dataRow?.TGL_M).format('DD MMMM YYYY')],
                    ['Pukul', dayjs(dataRow?.TGL_M).format('HH:mm')],
                    ['Usia', parseInt(dataRow?.UMUR) + ' Tahun']
                  ].map(([label, value]) => (
                    <Grid container key={label}>
                      <Grid item xs={5}>
                        <Typography variant='body2' color='textSecondary'>
                          {label}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant='body2'>{value}</Typography>
                      </Grid>
                    </Grid>
                  ))}

                  <Box mt={3}>
                    <Typography variant='subtitle2' color='primary' gutterBottom>
                      DISEMAYAMKAN DI
                    </Typography>
                    {[
                      ['Lokasi', 'TMC Eternal Home'],
                      ['Ruangan', dataRow?.NAMA_RUANG]
                    ].map(([label, value]) => (
                      <Grid container key={label}>
                        <Grid item xs={5}>
                          <Typography variant='body2' color='textSecondary'>
                            {label}
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant='body2'>{value}</Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>

                  <Box mt={3}>
                    <Typography variant='subtitle2' color='primary' gutterBottom>
                      ACARA
                    </Typography>
                    {[
                      ['Ibadah Tutup Peti', ''],
                      [
                        'Hari & Tanggal',
                        dataRow?.TGL_TUTUPPETI ? dayjs(dataRow?.TGL_TUTUPPETI).format('dddd, DD MMMM YYYY') : '-'
                      ],
                      ['Pukul', dataRow?.TGL_TUTUPPETI ? dayjs(dataRow?.TGL_TUTUPPETI).format('HH:mm') : '-'],
                      ['Upacara Pemberangkatan', ''],
                      [
                        'Hari & Tanggal',
                        dataRow?.TGL_BERANGKAT ? dayjs(dataRow?.TGL_BERANGKAT).format('dddd, DD MMMM YYYY') : '-'
                      ],
                      ['Pukul', dataRow?.TGL_BERANGKAT ? dayjs(dataRow?.TGL_BERANGKAT).format('HH:mm') : '-']
                    ].map(([label, value]) => (
                      <Grid container key={label}>
                        <Grid item xs={value === '' ? 12 : 5}>
                          <Typography
                            variant='body2'
                            color={value === '' ? 'primary' : 'textSecondary'}
                            sx={{ fontWeight: value === '' ? 'bold' : 'regular' }}
                          >
                            {label}
                          </Typography>
                        </Grid>
                        {value !== '' && (
                          <Grid item xs={7}>
                            <Typography variant='body2'>{value}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                  </Box>

                  {/* <Box mt={3}>
                    <Typography variant='subtitle2' color='primary' gutterBottom>
                      PEMAKAMAN
                    </Typography>
                    {[
                      ['Tempat', '-'],
                      ['Hari & Tanggal', '-'],
                      ['Pukul', '-']
                    ].map(([label, value]) => (
                      <Grid container key={label}>
                        <Grid item xs={5}>
                          <Typography variant='body2' color='textSecondary'>
                            {label}
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant='body2'>{value}</Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Box> */}

                  <Divider sx={{ my: 3 }} />

                  <Typography variant='subtitle2' mb={1}>
                    BANTU SHARE KEPADA KERABAT LAINNYA
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                      justifyContent: {
                        xs: 'center',
                        sm: 'flex-end'
                      }
                    }}
                  >
                    <Button variant='outlined' startIcon={<WhatsAppIcon />} color='success' onClick={shareToWhatsApp}>
                      WhatsApp
                    </Button>
                    <Button variant='outlined' startIcon={<FacebookIcon />} color='primary' onClick={shareToFacebook}>
                      Facebook
                    </Button>
                  </Stack>
                </Paper>

                {/* Kirim Ucapan Section */}
                <Box mt={4}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant='subtitle1' gutterBottom>
                      KIRIM UCAPAN DUKA UNTUK DITAYANG
                    </Typography>
                    <Typography variant='body2' color='textSecondary' mb={2}>
                      Tanpa mengurangi rasa hormat, dalam rangka mendukung upaya ramah lingkungan, bagi yang ingin
                      mengirim bunga papan, dapat mengirimkan bunga papan digital melalui{' '}
                      <strong>TMC Eternal Home</strong> yang akan ditampilkan di ruangan persemayaman.
                    </Typography>

                    <Box display='flex' justifyContent='center'>
                      <CustomButton type='button' loading={false} onClick={handleOpen}>
                        Kirim Ucapan Duka
                      </CustomButton>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </form>
      </Dialog>
    </>
  )
}

export default DetailDialog
